import * as React from 'react';
import { Folder, File, Shield, Car, Home, Heart, Building2, Users, FileText, DollarSign } from 'lucide-react';
import { TreeView } from '@components/base/data';
import { Text, Badge } from '@components/base/display';
import { VStack, HStack, Card } from '@components/base/layout';
import { Button } from '@components/base/actions';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

function TreeViewShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  const selectable = playgroundProps.selectable as boolean;
  const multiSelect = playgroundProps.multiSelect as boolean;

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="tree view">
          <Card className="max-w-md p-4">
            <TreeView
              selectable={selectable}
              multiSelect={multiSelect}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
            >
              <TreeView.Node id="policies" label="Insurance Policies" icon={<Shield className="w-4 h-4" />} defaultExpanded>
                <TreeView.Node id="auto" label="Auto Insurance" icon={<Car className="w-4 h-4" />} />
                <TreeView.Node id="home" label="Home Insurance" icon={<Home className="w-4 h-4" />} />
                <TreeView.Node id="life" label="Life Insurance" icon={<Heart className="w-4 h-4" />} />
              </TreeView.Node>
            </TreeView>
            {selectable && selectedIds.length > 0 && (
              <Text size="sm" color="secondary" className="mt-3 pt-3 border-t border-[#E8E6E3]">
                Selected: {selectedIds.join(', ')}
              </Text>
            )}
          </Card>
      </ShowcasePreviewSection>

      {/* Basic Tree */}
      <ShowcaseDemoSection
        title="Basic Tree"
        description="Simple hierarchical tree structure with expand/collapse"
       
      >
          <Card className="max-w-md p-4">
            <TreeView>
              <TreeView.Node id="documents" label="Documents">
                <TreeView.Node id="docs-policies" label="Policies">
                  <TreeView.Node id="docs-auto" label="Auto_Policy_2024.pdf" />
                  <TreeView.Node id="docs-home" label="Home_Policy_2024.pdf" />
                </TreeView.Node>
                <TreeView.Node id="docs-claims" label="Claims">
                  <TreeView.Node id="docs-claim1" label="Claim_001.pdf" />
                  <TreeView.Node id="docs-claim2" label="Claim_002.pdf" />
                </TreeView.Node>
              </TreeView.Node>
              <TreeView.Node id="reports" label="Reports">
                <TreeView.Node id="reports-2024" label="2024">
                  <TreeView.Node id="reports-q1" label="Q1_Report.xlsx" />
                  <TreeView.Node id="reports-q2" label="Q2_Report.xlsx" />
                </TreeView.Node>
              </TreeView.Node>
            </TreeView>
          </Card>
      </ShowcaseDemoSection>

      {/* Single Selection */}
      <ShowcaseDemoSection
        title="Single Selection"
        description="Tree with single item selection mode"
       
      >
          <Card className="max-w-md p-4">
            <TreeView
              selectable
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
            >
              <TreeView.Node id="coverage-types" label="Coverage Types" defaultExpanded>
                <TreeView.Node id="liability" label="Liability Coverage" />
                <TreeView.Node id="collision" label="Collision Coverage" />
                <TreeView.Node id="comprehensive" label="Comprehensive Coverage" />
                <TreeView.Node id="medical" label="Medical Payments" />
                <TreeView.Node id="uninsured" label="Uninsured Motorist" />
              </TreeView.Node>
            </TreeView>
            {selectedIds.length > 0 && (
              <HStack gap={2} align="center" className="mt-3 pt-3 border-t border-[#E8E6E3]">
                <Text size="sm" color="secondary">Selected:</Text>
                <Badge variant="default">{selectedIds[0]}</Badge>
              </HStack>
            )}
          </Card>
      </ShowcaseDemoSection>

      {/* Multi Selection */}
      <ShowcaseDemoSection
        title="Multi Selection"
        description="Tree with multiple item selection support"
       
      >
          <Card className="max-w-md p-4">
            <VStack gap={3}>
              <TreeView
                selectable
                multiSelect
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
              >
                <TreeView.Node id="add-ons" label="Optional Add-ons" defaultExpanded>
                  <TreeView.Node id="roadside" label="Roadside Assistance" />
                  <TreeView.Node id="rental" label="Rental Car Reimbursement" />
                  <TreeView.Node id="gap" label="Gap Insurance" />
                  <TreeView.Node id="custom-parts" label="Custom Parts Coverage" />
                  <TreeView.Node id="new-car" label="New Car Replacement" />
                </TreeView.Node>
              </TreeView>
              <HStack justify="between" align="center" className="pt-3 border-t border-[#E8E6E3]">
                <Text size="sm" color="secondary">
                  {selectedIds.length} add-on{selectedIds.length === 1 ? '' : 's'} selected
                </Text>
                {selectedIds.length > 0 && (
                  <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>
                    Clear
                  </Button>
                )}
              </HStack>
            </VStack>
          </Card>
      </ShowcaseDemoSection>

      {/* With Icons */}
      <ShowcaseDemoSection
        title="With Icons"
        description="Tree nodes with custom icons for better visual hierarchy"
       
      >
          <Card className="max-w-md p-4">
            <TreeView>
              <TreeView.Node
                id="company"
                label="Kusudama Insurance"
                icon={<Building2 className="w-4 h-4" />}
                defaultExpanded
              >
                <TreeView.Node
                  id="departments"
                  label="Departments"
                  icon={<Users className="w-4 h-4" />}
                  defaultExpanded
                >
                  <TreeView.Node
                    id="dept-claims"
                    label="Claims Processing"
                    icon={<FileText className="w-4 h-4" />}
                  />
                  <TreeView.Node
                    id="dept-underwriting"
                    label="Underwriting"
                    icon={<Shield className="w-4 h-4" />}
                  />
                  <TreeView.Node
                    id="dept-finance"
                    label="Finance"
                    icon={<DollarSign className="w-4 h-4" />}
                  />
                </TreeView.Node>
                <TreeView.Node
                  id="products"
                  label="Products"
                  icon={<Shield className="w-4 h-4" />}
                >
                  <TreeView.Node
                    id="prod-personal"
                    label="Personal Lines"
                    icon={<Users className="w-4 h-4" />}
                  />
                  <TreeView.Node
                    id="prod-commercial"
                    label="Commercial Lines"
                    icon={<Building2 className="w-4 h-4" />}
                  />
                </TreeView.Node>
              </TreeView.Node>
            </TreeView>
          </Card>
      </ShowcaseDemoSection>

      {/* Default Expanded */}
      <ShowcaseDemoSection
        title="Default Expanded"
        description="Tree with certain nodes expanded by default"
       
      >
          <Card className="max-w-md p-4">
            <TreeView>
              <TreeView.Node
                id="file-structure"
                label="Policy Documents"
                icon={<Folder className="w-4 h-4" />}
                defaultExpanded
              >
                <TreeView.Node
                  id="current-year"
                  label="2024"
                  icon={<Folder className="w-4 h-4" />}
                  defaultExpanded
                >
                  <TreeView.Node id="policy-auto" label="Auto_Insurance.pdf" icon={<File className="w-4 h-4" />} />
                  <TreeView.Node id="policy-home" label="Home_Insurance.pdf" icon={<File className="w-4 h-4" />} />
                </TreeView.Node>
                <TreeView.Node
                  id="archive"
                  label="Archive"
                  icon={<Folder className="w-4 h-4" />}
                >
                  <TreeView.Node id="archive-2023" label="2023" icon={<Folder className="w-4 h-4" />} />
                  <TreeView.Node id="archive-2022" label="2022" icon={<Folder className="w-4 h-4" />} />
                </TreeView.Node>
              </TreeView.Node>
            </TreeView>
          </Card>
      </ShowcaseDemoSection>

      {/* Disabled Nodes */}
      <ShowcaseDemoSection
        title="Disabled Nodes"
        description="Tree with some nodes disabled"
       
      >
          <Card className="max-w-md p-4">
            <TreeView selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds}>
              <TreeView.Node id="policy-options" label="Policy Options" defaultExpanded>
                <TreeView.Node id="opt-basic" label="Basic Coverage" />
                <TreeView.Node id="opt-standard" label="Standard Coverage" />
                <TreeView.Node id="opt-premium" label="Premium Coverage" />
                <TreeView.Node id="opt-platinum" label="Platinum Coverage" disabled />
              </TreeView.Node>
            </TreeView>
            <Text size="xs" color="muted" className="mt-3 pt-3 border-t border-[#E8E6E3]">
              Note: Platinum Coverage is disabled and cannot be selected
            </Text>
          </Card>
      </ShowcaseDemoSection>

      {/* Insurance Products Hierarchy */}
      <ShowcaseDemoSection
        title="Insurance Products Hierarchy"
        description="Real-world insurance product catalog organized as a tree"
       
      >
          <VStack gap={4}>
            <HStack justify="between" align="center" className="px-1">
              <Text size="lg" weight="semibold">Product Catalog</Text>
              <Text size="sm" color="secondary">
                {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select products to compare'}
              </Text>
            </HStack>
            <Card className="p-4">
              <TreeView
                selectable
                multiSelect
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
              >
                <TreeView.Node
                  id="personal-lines"
                  label="Personal Lines"
                  icon={<Users className="w-4 h-4" />}
                  defaultExpanded
                >
                  <TreeView.Node
                    id="auto-insurance"
                    label="Auto Insurance"
                    icon={<Car className="w-4 h-4" />}
                    defaultExpanded
                  >
                    <TreeView.Node id="auto-liability" label="Liability Coverage" />
                    <TreeView.Node id="auto-collision" label="Collision Coverage" />
                    <TreeView.Node id="auto-comprehensive" label="Comprehensive Coverage" />
                    <TreeView.Node id="auto-medical" label="Medical Payments" />
                  </TreeView.Node>
                  <TreeView.Node
                    id="home-insurance"
                    label="Home Insurance"
                    icon={<Home className="w-4 h-4" />}
                  >
                    <TreeView.Node id="home-dwelling" label="Dwelling Coverage" />
                    <TreeView.Node id="home-personal" label="Personal Property" />
                    <TreeView.Node id="home-liability" label="Personal Liability" />
                    <TreeView.Node id="home-loss-of-use" label="Loss of Use" />
                  </TreeView.Node>
                  <TreeView.Node
                    id="life-insurance"
                    label="Life Insurance"
                    icon={<Heart className="w-4 h-4" />}
                  >
                    <TreeView.Node id="life-term" label="Term Life" />
                    <TreeView.Node id="life-whole" label="Whole Life" />
                    <TreeView.Node id="life-universal" label="Universal Life" />
                  </TreeView.Node>
                </TreeView.Node>
                <TreeView.Node
                  id="commercial-lines"
                  label="Commercial Lines"
                  icon={<Building2 className="w-4 h-4" />}
                >
                  <TreeView.Node
                    id="business-property"
                    label="Business Property"
                    icon={<Building2 className="w-4 h-4" />}
                  >
                    <TreeView.Node id="bop" label="Business Owner's Policy" />
                    <TreeView.Node id="commercial-property" label="Commercial Property" />
                  </TreeView.Node>
                  <TreeView.Node
                    id="business-liability"
                    label="Liability"
                    icon={<Shield className="w-4 h-4" />}
                  >
                    <TreeView.Node id="general-liability" label="General Liability" />
                    <TreeView.Node id="professional-liability" label="Professional Liability" />
                    <TreeView.Node id="cyber-liability" label="Cyber Liability" />
                  </TreeView.Node>
                  <TreeView.Node
                    id="workers-comp"
                    label="Workers' Compensation"
                    icon={<Users className="w-4 h-4" />}
                  />
                </TreeView.Node>
              </TreeView>
            </Card>
            {selectedIds.length > 0 && (
              <Card className="p-3 bg-[#F0F4F8]">
                <VStack gap={2}>
                  <Text size="sm" weight="medium">Selected Products:</Text>
                  <HStack gap={2} wrap="wrap">
                    {selectedIds.map((id) => (
                      <Badge key={id} variant="default" size="sm">
                        {id.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </Badge>
                    ))}
                  </HStack>
                  <HStack gap={2} className="mt-2">
                    <Button size="sm" variant="secondary">Compare Selected</Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>
                      Clear Selection
                    </Button>
                  </HStack>
                </VStack>
              </Card>
            )}
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const TreeViewShowcase: ShowcaseDefinition = {
  id: 'tree-view',
  name: 'TreeView',
  category: 'data',
  description: 'Hierarchical tree structure for nested data',
  propsDefinition: [
    {
      name: 'selectable',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether nodes can be selected',
    },
    {
      name: 'multiSelect',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether multiple nodes can be selected (requires selectable)',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="tree" on container',
      'role="treeitem" on nodes',
      'aria-expanded on expandable nodes',
      'aria-selected on selectable nodes',
      'aria-level indicating nesting depth',
      'aria-disabled on disabled nodes',
    ],
    keyboardNav: [
      'ArrowDown: Move to next visible node',
      'ArrowUp: Move to previous visible node',
      'ArrowRight: Expand node or move to first child',
      'ArrowLeft: Collapse node or move to parent',
      'Enter/Space: Select node or trigger onClick',
      'Home: Move to first node',
      'End: Move to last visible node',
    ],
    notes: [
      'Full keyboard navigation with roving tabindex',
      'Focus management across expand/collapse',
      'Screen reader announces node state changes',
      'Visual indicators for selected and focused nodes',
    ],
  },
  component: TreeViewShowcaseComponent,
};
